import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { getUserLocation, UserLocationResult } from "@/utils/location";

export interface LocationState {
  countryCode: string | null;
  countryName?: string;
  isIndia: boolean;
  isInternational: boolean;
  isLoaded: boolean;
}

const getInitialLocationState = (): LocationState => {
  if (typeof window !== "undefined") {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const intlParam = urlParams.get("intl");
      if (intlParam === "1") {
        return {
          countryCode: "US",
          countryName: "United States",
          isIndia: false,
          isInternational: true,
          isLoaded: true,
        };
      }
      if (intlParam === "0") {
        return {
          countryCode: "IN",
          countryName: "India",
          isIndia: true,
          isInternational: false,
          isLoaded: true,
        };
      }
      // Check timezone directly: If browser timezone is non-Indian (such as Europe/London), it is international immediately!
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const isIndiaTz =
        tz === "Asia/Kolkata" || tz === "Asia/Calcutta" || tz === "IST";
      if (tz && !isIndiaTz) {
        return {
          countryCode: tz === "Europe/London" ? "GB" : "INTL",
          countryName:
            tz === "Europe/London" ? "United Kingdom" : "International",
          isIndia: false,
          isInternational: true,
          isLoaded: true,
        };
      }

      const cached = localStorage.getItem("nakman_user_location");
      if (cached) {
        const parsed = JSON.parse(cached);
        const isIndia = parsed.isIndia ?? (parsed.countryCode === "IN");
        return {
          countryCode: parsed.countryCode || "IN",
          countryName: parsed.countryName,
          isIndia: isIndia,
          isInternational: !isIndia,
          isLoaded: true,
        };
      }
    } catch {
      // ignore
    }
  }
  return {
    countryCode: "IN",
    countryName: "India",
    isIndia: true,
    isInternational: false,
    isLoaded: false,
  };
};

const initialState: LocationState = getInitialLocationState();

export const detectLocationAction = createAsyncThunk(
  "location/detectLocation",
  async () => {
    const loc = await getUserLocation();
    return loc;
  },
);

const locationSlice = createSlice({
  name: "location",
  initialState,
  reducers: {
    setLocation: (state, action: PayloadAction<UserLocationResult>) => {
      state.countryCode = action.payload.countryCode;
      state.countryName = action.payload.countryName;
      state.isIndia = action.payload.isIndia;
      state.isInternational = !action.payload.isIndia;
      state.isLoaded = true;
    },
    setInternationalOverride: (state, action: PayloadAction<boolean>) => {
      state.isInternational = action.payload;
      state.isIndia = !action.payload;
      state.isLoaded = true;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(detectLocationAction.fulfilled, (state, action) => {
      state.countryCode = action.payload.countryCode;
      state.countryName = action.payload.countryName;
      state.isIndia = action.payload.isIndia;
      state.isInternational = !action.payload.isIndia;
      state.isLoaded = true;
    });
  },
});

export const { setLocation, setInternationalOverride } = locationSlice.actions;
export default locationSlice.reducer;
